import { useState } from "react";
import { http, API } from "../api";
import { Camera, X } from "lucide-react";

/**
 * Upload widget for a single image. Uploads to /api/uploads/{category}, returns file_id/url.
 *
 * Props:
 *  - category: profile | cover | voter_roll | scheme | form
 *  - value: current file_id (may be null/"")
 *  - onChange: (file_id | "") => void
 *  - shape: "circle" | "banner"
 *  - label: string
 */
export default function PhotoUpload({ category, value, onChange, shape = "circle", label = "Photo", testid = "" }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const src = value ? `${API}/files/${value}` : "";
  const isCircle = shape === "circle";

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post(`/uploads/${category}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.file_id);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid={testid || `upload-${category}`}>
      <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".3px", color: "var(--muted)" }}>{label}</span>
      <label style={{
        cursor: "pointer",
        width: isCircle ? 96 : "100%",
        height: isCircle ? 96 : 120,
        borderRadius: isCircle ? "50%" : 12,
        border: "1px dashed var(--line)",
        background: src ? `#eef center/cover no-repeat url(${src})` : "#f7fbf8",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "var(--muted)", position: "relative", overflow: "hidden",
      }}>
        {!src && (busy ? <span style={{ fontSize: 11 }}>Uploading…</span> : <Camera size={20} />)}
        <input type="file" accept="image/*" style={{ display: "none" }} onChange={handle} disabled={busy} />
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <label style={{ fontSize: 11, cursor: "pointer", color: "var(--teal, #0f766e)" }}>
          {src ? "Replace" : "Upload"}
          <input type="file" accept="image/*" style={{ display: "none" }} onChange={handle} disabled={busy} data-testid={`${testid || `upload-${category}`}-input`} />
        </label>
        {src && (
          <button type="button" style={{ fontSize: 11, color: "#b04a3f", background: "none", border: "none", cursor: "pointer", padding: 0 }} onClick={() => onChange("")} data-testid={`${testid || `upload-${category}`}-clear`}>
            <X size={11} /> Clear
          </button>
        )}
      </div>
      {err && <small style={{ color: "#b04a3f", fontSize: 11 }}>{err}</small>}
    </div>
  );
}
